import { PointerEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Dialog } from "@mui/material";
import { RootState } from "app/store";
import { DialogType } from "app/types";
import { actions } from "app/configSlice";
import I18n from "common/i18n";
import { CloseBtn, PlanBadge } from "common/elements";
import { useScale, btnDelay, usePlaySound, shouldApplyGuestRestrictions } from "common/utils";
import { usePlan } from "features/subscription";
import { AccountType } from "features/user/types";
import { UserPageTab, PlanSubTab } from "features/user/page/UserPageTab";
import { DashboardPlanUsage } from "./DashboardPlanUsage";
import { DashboardMailPreview } from "./DashboardMailPreview";
import { DashboardAccountSwitch } from "./DashboardAccountSwitch";
import { DashboardQuickSettings } from "./DashboardQuickSettings";

const SakidoriBadge = () => (
  <div className="flex-row-el flex-center rounded-full p-2" style={{ backgroundColor: "#9575CD" }}>
    <div className="flex-row-el flex-center">
      <div
        className="flex-col-center w-[24px] h-[24px] rounded-full mr-1"
        style={{ backgroundColor: "#9575CD" }}
      >
        <span className="material-symbols-outlined text-white type-body">workspace_premium</span>
      </div>
      <p className="type-text text-white type-body leading-tight">{I18n.t("MSG_PLAN_SAKIDORI")}</p>
    </div>
  </div>
);

const ActionCard = ({
  icon,
  label,
  onClick,
}: {
  icon: string;
  label: string;
  onClick: () => void;
}) => (
  <button className="type-inherit-font opacity-button" onClick={onClick}>
    <div
      className="flex-col-center w-[140px] h-full px-2 rounded-xl bg-white/60 border-2 border-solid border-beige-60 cursor-pointer"
      style={{ boxShadow: "0px 2px 0px #FFD1A1" }}
    >
      <div className="flex-col-center w-[48px] h-[48px]">
        <span className="material-symbols-outlined type-hero !leading-none text-textcolor-black">
          {icon}
        </span>
      </div>
      <p className="type-text type-sm text-textcolor-black leading-tight mt-1">{label}</p>
    </div>
  </button>
);

export const UserDashboard = () => {
  const play = usePlaySound();
  const plan = usePlan();
  const { scale } = useScale();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userProfile = useSelector((state: RootState) => state.config.dialog.user_profile);
  const active = useSelector((state: RootState) => state.user.active);
  const parent = useSelector((state: RootState) => state.user.parent);

  const isSakidori =
    parent?.account_type === AccountType.GUEST &&
    parent?.created_at &&
    !shouldApplyGuestRestrictions(parent.created_at);

  const handleClose = () => {
    play();
    dispatch(
      actions.updateDialog({
        type: DialogType.USER_PROFILE,
        value: false,
      }),
    );
  };

  const gotoPlanPurchase = () => {
    dispatch(actions.updateDialog({ type: DialogType.USER_PROFILE, value: false }));
    navigate(`/user?tab=${UserPageTab.PLAN}&subTab=${PlanSubTab.PURCHASE}`);
  };

  const handlePlanBadgeClick = (e: PointerEvent) => {
    e.stopPropagation();
    play();
    btnDelay(gotoPlanPurchase);
  };

  const handleUpgrade = () => {
    play();
    btnDelay(gotoPlanPurchase);
  };

  const handleOpenUserPage = () => {
    play();
    btnDelay(() => {
      dispatch(actions.updateDialog({ type: DialogType.USER_PROFILE, value: false }));
      navigate("/user");
    });
  };

  return (
    <Dialog
      fullScreen
      maxWidth={false}
      sx={{ zIndex: 10103 }}
      open={userProfile.value}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center !absolute inset-0 bg-textcolor-black/50 w-full h-full">
        <div
          style={{ transform: `scale(${scale})` }}
          className="flex-col-view items-center justify-start w-[920px] rounded-lg border-[8px] border-solid border-white bg-white"
        >
          <CloseBtn close={handleClose} />

          <div className="flex-col-el w-full relative rounded bg-beige-60 overflow-hidden">
            {/* タイトル */}
            <div className="flex-row-el items-center w-full px-6 pt-3 pb-1">
              <p className="type-text type-title text-textcolor-black">
                {I18n.t("MSG_USER_DASHBOARD_TITLE")}
              </p>
            </div>

            {/* ヘッダー: アバター + 名前 + PlanBadge + アクションボタン */}
            <div className="flex-row-el items-stretch justify-between w-full px-6 py-3 gap-2">
              {/* アバター + 名前 + バッジ */}
              <div className="flex-row-el items-center gap-4 bg-white/60 rounded-xl px-4 py-2">
                <div className="flex-col-center bg-white rounded-full w-[100px] h-[100px] shrink-0 shadow-[-1px_1px_1px_rgba(63,74,97,0.15),1px_1px_3px_rgba(63,74,97,0.25)]">
                  <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
                    <img
                      key={active?.icon}
                      alt="プレイヤープロフィール画像"
                      src={active?.icon}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                <div className="flex-col-el items-start gap-1 w-[250px]">
                  <div
                    className="cursor-pointer active:scale-95"
                    onPointerDown={handlePlanBadgeClick}
                  >
                    {isSakidori ? <SakidoriBadge /> : <PlanBadge plan={plan} />}
                  </div>
                  <p className="type-text type-body text-textcolor-black !text-left text-body-ellipsis w-full">
                    {active?.display_name}
                  </p>
                </div>
              </div>

              {/* アクションボタン */}
              <div className="flex-row-el items-stretch gap-3">
                <DashboardAccountSwitch />
                <ActionCard
                  icon="workspace_premium"
                  label={I18n.t("MSG_DASHBOARD_CHANGE_PLAN")}
                  onClick={handleUpgrade}
                />
                <ActionCard
                  icon="open_in_new"
                  label={I18n.t("MSG_USER_DASHBOARD_VIEW_FULL_PAGE")}
                  onClick={handleOpenUserPage}
                />
              </div>
            </div>

            {/* ゆうびんプレビュー */}
            <div className="flex-col-el items-center w-full px-6 py-3">
              <DashboardMailPreview />
            </div>

            {/* プラン利用状況 */}
            <div className="flex-col-el items-center w-full px-6 py-3">
              <DashboardPlanUsage />
            </div>

            {/* クイック設定 */}
            <div className="flex-row-el justify-center w-full px-6 py-3 mb-2">
              <DashboardQuickSettings />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
